import { XIcon } from 'lucide-react'
import { Dialog } from 'radix-ui'
import { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import { type OverlayId, type OverlayMeta, overlayContent, overlayMeta } from './registry'
import {
  getOverlay,
  type OverlayEntry,
  overlays,
  resetOverlays,
  takeOpener,
  useOverlays,
} from './store'

/**
 * The one place a dialog or drawer renders (#209), mounted once in AppShell.
 * It draws the store's stack (ADR-0019 D6), bottom first, each entry a layer of
 * its own. Every overlay gets, without asking for it:
 *
 * - a Radix Dialog: modal, focus trapped inside, Escape closes, a click on the
 *   backdrop closes (not for a confirm), `role`/`aria-modal`/labelling;
 * - stacking: only the top layer is interactive, Escape and the backdrop close
 *   only the top, and the layers underneath stay mounted and untouched — their
 *   state is the state the reader left. Radix's own layer stack routes Escape
 *   and the pointer; `inert` keeps the keyboard out of what is covered;
 * - the look of its variant — `dialog` centred, `drawer` the right-hand panel;
 * - lazy content, with the panel's skeleton while its chunk loads;
 * - focus back where it was when the entry opened — the button on the page, or
 *   the control in the drawer underneath — once the entry has gone;
 * - ⌘K / Ctrl+K toggling the command palette, and ⌘ or Ctrl alone starting
 *   to fetch it, so it is usually in cache by the time K lands.
 *
 * An entry the store has closed stays drawn, leaving, through its 200ms exit,
 * and only then leaves the DOM. Keys only grow, so ordering by key is the
 * stack's order with leaving entries where they stood: one sliding out on top,
 * or a replaced one under its replacement.
 */
export const EXIT_MS = 200 // must match duration-200 on the backdrop and panels

export function OverlayHost() {
  const stack = useOverlays()
  // In the DOM: the stack, plus entries still animating out. Follows the store
  // during render; an entry is dropped only once its exit has run.
  const [drawn, setDrawn] = useState<readonly OverlayEntry[]>(stack)
  const [seen, setSeen] = useState(stack)
  // Layers closed together with one under them: their openers are inside
  // what closed, so only the lowest closed layer hands focus back.
  const quiet = useRef(new Set<number>())
  if (stack !== seen) {
    const closed = seen.filter((e) => !stack.includes(e))
    for (const e of closed.slice(1)) quiet.current.add(e.key)
    setSeen(stack)
    setDrawn(withStack(drawn, stack))
  }
  const refocus = useRef<number[]>([])

  const onExited = useCallback((key: number) => {
    refocus.current.push(key)
    setDrawn((d) => d.filter((e) => e.key !== key))
  }, [])

  // After the panel has left the DOM, not before: while it is mounted its
  // focus scope still holds the page aria-hidden. When the opener has gone
  // meanwhile (the select item that pushed a drawer), the panel now on top
  // takes focus, so it stays inside the drawer underneath. A layer that closed
  // with one under it (quiet) hands nothing back: the lowest one does.
  // biome-ignore lint/correctness/useExhaustiveDependencies: `drawn` is the trigger — the commit that removed a layer — not an input.
  useEffect(() => {
    const keys = refocus.current
    if (keys.length === 0) return
    refocus.current = []
    for (const key of keys) {
      const el = takeOpener(key)
      if (quiet.current.delete(key) || el === undefined) continue
      const top = getOverlay()
      if (el?.isConnected) el.focus()
      else if (top) panelOf(top.key)?.focus()
    }
  }, [drawn])

  // Nothing drawn, nothing may block the page. Radix's modal layers set
  // `pointer-events: none` on <body> and restore it when the last one goes; a
  // menu closing while a dialog opens (a Select item that pushes a drawer) can
  // leave it set, and then no button on the page answers a click.
  useEffect(() => {
    if (drawn.length === 0 && document.body.style.pointerEvents === 'none') {
      document.body.style.removeProperty('pointer-events')
    }
  }, [drawn])

  // Every signed-in page mounts its own shell, so this host goes with the page.
  // An overlay open at that moment belongs to the page that is leaving (a back
  // navigation with a drawer up): it must not reappear on the next one.
  useEffect(() => () => resetOverlays(), [])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Meta' || e.key === 'Control') overlays.preload('palette')
      else if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        overlays.toggle('palette')
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  const top = stack.at(-1)
  return drawn.map((entry, depth) => (
    <OverlayLayer
      key={entry.key}
      entry={entry}
      open={stack.includes(entry)}
      top={entry === top}
      depth={depth}
      onExited={onExited}
    />
  ))
}

/**
 * What is drawn once the stack has changed: what was, plus what is new, in key
 * order. A new bottom means `open` replaced the stack: what it replaced goes at
 * once, not through an exit, so there is one panel on the page, as before.
 */
function withStack(
  drawn: readonly OverlayEntry[],
  stack: readonly OverlayEntry[],
): readonly OverlayEntry[] {
  const known = new Set(drawn.map((e) => e.key))
  const added = stack.filter((e) => !known.has(e.key))
  if (added.length === 0) return drawn
  if (stack[0] === added[0]) return stack
  return [...drawn, ...added].sort((a, b) => a.key - b.key)
}

function isEvent(v: unknown): boolean {
  return typeof v === 'object' && v !== null && ('nativeEvent' in v || v instanceof Event)
}

function panelOf(key: number): HTMLElement | null {
  return document.querySelector<HTMLElement>(`[data-slot="overlay"][data-overlay-key="${key}"]`)
}

/**
 * One entry of the stack on screen. Open/close is the website's small state
 * machine (apps/website ModalHost), per layer: `entered` flips a frame after
 * mount so the enter transition has a start state; `shown` is derived —
 * entered AND still open — so closing starts the exit at once. Radix is
 * force-mounted throughout: it provides the behaviour, this owns presence.
 */
function OverlayLayer({
  entry,
  open,
  top,
  depth,
  onExited,
}: {
  entry: OverlayEntry
  open: boolean
  top: boolean
  depth: number
  onExited: (key: number) => void
}) {
  const [entered, setEntered] = useState(false)
  const shown = entered && open
  // Chosen once per entry: lazy until its chunk has landed, direct after.
  const Content = useMemo(() => overlayContent(entry.id), [entry.id])
  const { key } = entry

  useEffect(() => {
    if (open) {
      const raf = requestAnimationFrame(() => setEntered(true))
      return () => cancelAnimationFrame(raf)
    }
    const t = window.setTimeout(() => onExited(key), EXIT_MS)
    return () => window.clearTimeout(t)
  }, [open, key, onExited])

  // `onClick={close}` hands over a click event, which is nobody's result.
  const close = useCallback(
    (result?: unknown) => overlays.close(key, isEvent(result) ? undefined : result),
    [key],
  )
  const meta = overlayMeta(entry.id)
  const alert = meta.role === 'alertdialog'
  // Later layers paint over earlier ones, whatever order their portals landed in.
  const z = 50 + depth * 2

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        if (!next) close()
      }}
    >
      <Dialog.Portal forceMount>
        <Dialog.Overlay
          forceMount
          data-slot="overlay-backdrop"
          style={{ zIndex: z }}
          // Only the bottom layer dims the page: a layer on top keeps its
          // backdrop to catch the click outside, but clear, so stacking a
          // drawer never darkens the page twice.
          data-dim={depth === 0 ? '' : undefined}
          className={cn(
            'fixed inset-0 transition-opacity duration-200 ease-out motion-reduce:transition-none',
            depth === 0 ? 'bg-black/50' : 'bg-transparent',
            shown ? 'opacity-100' : 'pointer-events-none opacity-0',
          )}
        />
        <Dialog.Content
          forceMount
          role={meta.role ?? 'dialog'}
          data-slot="overlay"
          data-variant={meta.variant}
          data-size={meta.variant === 'drawer' ? (meta.size ?? 'standard') : undefined}
          data-overlay-key={key}
          data-top={top ? '' : undefined}
          // Covered by another layer: out of the tab order and the pointer's
          // way until it is the top again. Its state stays as it was.
          inert={open && !top}
          style={{ zIndex: z + 1 }}
          // Focus goes back to the OPENER, which the store recorded; Radix's own
          // return would aim at whatever held focus when the panel mounted.
          onCloseAutoFocus={(e) => e.preventDefault()}
          onPointerDownOutside={(e) => {
            if (alert) e.preventDefault()
          }}
          onInteractOutside={(e) => {
            if (alert) e.preventDefault()
          }}
          className={cn(panelClass(meta, shown), !open && 'pointer-events-none')}
        >
          <Suspense fallback={<PanelSkeleton id={entry.id} meta={meta} />}>
            <Content key={key} {...(entry.props as Record<string, unknown>)} close={close} />
          </Suspense>
          {meta.plain || alert ? null : (
            <Dialog.Close
              className={cn(
                'absolute top-3 right-3 inline-flex size-8 items-center justify-center rounded-md text-muted-foreground',
                'transition-colors duration-[var(--motion-interaction)] ease-out hover:bg-accent hover:text-foreground',
                'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
                'motion-reduce:transition-none',
              )}
            >
              <XIcon className="size-4" aria-hidden />
              <span className="sr-only">Close</span>
            </Dialog.Close>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

/**
 * A panel's classes, from its registry meta alone. A drawer is full width on a
 * phone; from `sm` it is `max-w-md`, or, with the `wide` size, `max-w-3xl`
 * growing to `max-w-4xl` on a desktop: room for two columns side by side.
 */
export function panelClass(meta: OverlayMeta, shown: boolean): string {
  const motion = 'duration-200 ease-out outline-none motion-reduce:transition-none'
  if (meta.variant === 'drawer') {
    return cn(
      'fixed inset-y-0 right-0 flex h-full w-full flex-col border-l bg-background shadow-lg',
      meta.size === 'wide' ? 'sm:max-w-3xl lg:max-w-4xl' : 'sm:max-w-md',
      'transition-[translate]',
      motion,
      shown ? 'translate-x-0' : 'translate-x-full',
    )
  }
  if (meta.placement === 'top') {
    return cn(
      'fixed top-[16vh] left-1/2 w-[calc(100%-2rem)] max-w-xl origin-top -translate-x-1/2',
      'transition-[opacity,scale,translate]',
      motion,
      shown ? 'translate-y-0 scale-100 opacity-100' : '-translate-y-2 scale-[0.98] opacity-0',
    )
  }
  return cn(
    'fixed top-1/2 left-1/2 grid w-full max-w-[calc(100%-2rem)] -translate-x-1/2 -translate-y-1/2 gap-4 sm:max-w-lg',
    !meta.plain && 'rounded-lg border bg-background p-6 shadow-lg',
    'transition-[opacity,scale]',
    motion,
    shown ? 'scale-100 opacity-100' : 'scale-95 opacity-0',
  )
}

/**
 * What the panel shows while its chunk loads: the shape of a header and a body,
 * and the overlay's name for a screen reader, which the content replaces with
 * its own title once it lands.
 */
function PanelSkeleton({ id, meta }: { id: OverlayId; meta: OverlayMeta }) {
  const title = <Dialog.Title className="sr-only">{meta.label}</Dialog.Title>
  if (meta.variant !== 'drawer') {
    return (
      <div data-slot="overlay-skeleton" data-overlay={id} className="flex flex-col gap-3 p-4">
        {title}
        <Skeleton className="h-5 w-40" />
        <Skeleton className="h-4 w-64 max-w-full" />
        <Skeleton className="h-16 w-full" />
      </div>
    )
  }
  // A drawer's placeholder is the shape of a FormDrawer (overlays/form-drawer.tsx):
  // header, two fields, the actions. Thin lines where text will be, never one
  // big block, so the form lands where its placeholder already stood.
  return (
    <div data-slot="overlay-skeleton" data-overlay={id} className="flex min-h-0 flex-1 flex-col">
      {title}
      <div className="flex shrink-0 flex-col gap-2 border-b p-4 pr-12">
        <Skeleton className="h-5 w-32" />
        <Skeleton className="h-3.5 w-full" />
        <Skeleton className="h-3.5 w-3/4" />
      </div>
      <div className="flex flex-1 flex-col gap-6 p-4">
        {[0, 1].map((i) => (
          <div key={i} className="flex flex-col gap-2">
            <Skeleton className="h-3.5 w-20" />
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-3 w-2/3" />
          </div>
        ))}
      </div>
      <div className="mt-auto flex shrink-0 flex-col gap-2 border-t p-4">
        <Skeleton className="h-9 w-full" />
        <Skeleton className="h-9 w-full" />
      </div>
    </div>
  )
}
